import React, { useEffect, useState } from 'react'
import { Calendar, momentLocalizer } from 'react-big-calendar'
import moment from 'moment'
import styled from 'styled-components'
import { colors } from '../../../Shared/colors'
import "react-big-calendar/lib/css/react-big-calendar.css";
import { useGetTelesalesCalendarTasksQuery } from '../../../features/telesaletaskSlice'

const localizer = momentLocalizer(moment)

export const TelesalesCalendar = () => {
  const [view, setView] = useState('month')
  const [date, setDate] = useState(new Date())
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [showEventModal, setShowEventModal] = useState(false)

  // Get all tasks for calendar display
  const { data: allTasks, isLoading, error } = useGetTelesalesCalendarTasksQuery()


  // Convert tasks to calendar events
  const myEventsList = []

  allTasks?.forEach(task => {
    if (task.nextTask?.deadline) {
      const deadline = new Date(task.nextTask.deadline)

      // Only add events with valid dates
      if (!isNaN(deadline.getTime())) {
        const event = {
          id: task._id,
          title: `${task.nextTask.nextTaskType} - Lead #${task.leadID?.leadID}`,
          start: deadline,
          end: new Date(deadline.getTime() + 60 * 60 * 1000), // 1 hour duration
          resource: {
            taskId: task._id,
            leadId: task.leadID?.leadID,
            taskType: task.nextTask?.nextTaskType,
            clientName: task.leadID?.clientID?.fullName,
            clientPhone: task.leadID?.clientID?.cellNo?.[0]?.phoneNo,
            project: task.leadID?.leadOpportunity?.project?.projectName,
            classification: task.leadID?.leadOpportunity?.classification,
            deadline: task.nextTask?.deadline,
          }
        };

        myEventsList.push(event);
      }
    }
  })

  // Handle event selection
  const handleSelectEvent = (event) => {
    setSelectedEvent(event)
    setShowEventModal(true)
  }

  // Custom event component
  const CustomEvent = ({ event }) => {
    const getEventColor = (taskType) => {
      switch (taskType) {
        case 'meeting_schedule':
          return '#4CAF50' // Green
        case 'followUp':
          return '#FF9800' // Orange
        case 'meeting_reschedule':
          return '#F44336' // Red
        default:
          return '#2196F3' // Blue
      }
    }

    const color = getEventColor(event.resource?.taskType)

    return (
      <EventContainer style={{ backgroundColor: color }}>
        <EventTitle>{event.title}</EventTitle>
        <EventTime>
          {moment(event.start).format('HH:mm')}
        </EventTime>
      </EventContainer>
    )
  }

  // Custom toolbar
  const CustomToolbar = ({ label, onNavigate, onView }) => (
    <ToolbarContainer>
      <ToolbarLeft>
        <NavigationButton onClick={() => onNavigate('PREV')}>
          <span>‹</span>
        </NavigationButton>
        <NavigationButton onClick={() => onNavigate('NEXT')}>
          <span>›</span>
        </NavigationButton>
        <TodayButton onClick={() => onNavigate('TODAY')}>
          Today
        </TodayButton>
      </ToolbarLeft>

      <ToolbarCenter>
        <MonthLabel>{label}</MonthLabel>
      </ToolbarCenter>

      <ToolbarRight>
        <ViewButton
          active={view === 'month'}
          onClick={() => onView('month')}
        >
          Month
        </ViewButton>
        <ViewButton
          active={view === 'week'}
          onClick={() => onView('week')}
        >
          Week
        </ViewButton>
        <ViewButton
          active={view === 'day'}
          onClick={() => onView('day')}
        >
          Day
        </ViewButton>
        <ViewButton
          active={view === 'agenda'}
          onClick={() => onView('agenda')}
        >
          Agenda
        </ViewButton>
      </ToolbarRight>
    </ToolbarContainer>
  )

  // Event details modal
  const EventModal = () => {
    if (!showEventModal || !selectedEvent) return null

    return (
      <ModalOverlay onClick={() => setShowEventModal(false)}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>📋 Task Details</ModalTitle>
            <CloseButton onClick={() => setShowEventModal(false)}>×</CloseButton>
          </ModalHeader>

          <ModalBody>
            <DetailRow>
              <DetailLabel>Task Type:</DetailLabel>
              <DetailValue>{selectedEvent.resource?.taskType}</DetailValue>
            </DetailRow>

            <DetailRow>
              <DetailLabel>Lead ID:</DetailLabel>
              <DetailValue>#{selectedEvent.resource?.leadId}</DetailValue>
            </DetailRow>

            <DetailRow>
              <DetailLabel>Client Name:</DetailLabel>
              <DetailValue>{selectedEvent.resource?.clientName || 'N/A'}</DetailValue>
            </DetailRow>

            <DetailRow>
              <DetailLabel>Phone Number:</DetailLabel>
              <DetailValue>
                {selectedEvent.resource?.clientPhone ? (
                  <PhoneLink href={`tel:${selectedEvent.resource.clientPhone}`}>
                    {selectedEvent.resource.clientPhone}
                  </PhoneLink>
                ) : 'N/A'}
              </DetailValue>
            </DetailRow>

            <DetailRow>
              <DetailLabel>Project:</DetailLabel>
              <DetailValue>{selectedEvent.resource?.project || 'N/A'}</DetailValue>
            </DetailRow>

            <DetailRow>
              <DetailLabel>Classification:</DetailLabel>
              <DetailValue>{selectedEvent.resource?.classification || 'N/A'}</DetailValue>
            </DetailRow>

            <DetailRow>
              <DetailLabel>Deadline:</DetailLabel>
              <DetailValue>
                {moment(selectedEvent.resource?.deadline).format('MMMM DD, YYYY HH:mm')}
              </DetailValue>
            </DetailRow>

          </ModalBody>

          <ModalFooter>
            <ActionButton onClick={() => setShowEventModal(false)}>
              Close
            </ActionButton>
          </ModalFooter>
        </ModalContent>
      </ModalOverlay>
    )
  }

  useEffect(() => {
    if (showEventModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showEventModal]);


  if (isLoading) {
    return (
      <Main>
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>Loading calendar events...</LoadingText>
        </LoadingContainer>
      </Main>
    )
  }

  return (
    <Main>
      <CalendarContainer>
        <Calendar
          localizer={localizer}
          events={myEventsList}
          step={60}
          date={date}
          onNavigate={setDate}
          view={view}
          onView={setView}
          defaultDate={new Date()}
          startAccessor="start"
          endAccessor="end"
          onSelectEvent={handleSelectEvent}
          components={{
            event: CustomEvent,
            toolbar: CustomToolbar,
          }}
          views={['month', 'week', 'day', 'agenda']}
          showMultiDayTimes
          popupOffset={{ x: 10, y: 10 }}
        />
      </CalendarContainer>

      <EventModal />
    </Main>
  )
}

// Styled Components
const Main = styled.div`
  width: 100%;
  height: calc(100vh - 10vh);
  padding: 1rem;
  background: linear-gradient(135deg, ${colors.lightWhite} 0%, ${colors.inputWhite} 100%);
  border-radius: 16px;
  overflow: hidden;
  position: relative;
`;


const CalendarContainer = styled.div`
  height: calc(100% - 2rem);
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  
  /* Hide default toolbar */
  .rbc-toolbar {
    display: none;
  }
  
  /* Custom month view styles */
  .rbc-month-view {
    border: none;
    background: white;
  }
  
  .rbc-header {
    background: linear-gradient(135deg, ${colors.brown} 0%, #1a6b6b 100%);
    color: white;
    font-weight: 600;
    font-size: 0.9rem;
    padding: 12px 8px;
    text-align: center;
    border: none;
    min-height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .rbc-date-cell {
    padding: 8px;
    font-weight: 500;
    color: ${colors.text};
    font-size: 0.9rem;
    min-height: 35px;
    display: flex;
    align-items: center;
  }
  
  .rbc-today {
    background: rgba(36, 147, 147, 0.1);
  }
  
  .rbc-today .rbc-date-cell {
    color: ${colors.brown};
    font-weight: 700;
  }
  
  .rbc-month-row {
    min-height: 100px;
  }
  
  /* Event styles */
  .rbc-event {
    border: none !important;
    border-radius: 6px !important;
    padding: 4px 8px !important;
    margin: 2px !important;
    font-size: 0.75rem !important;
    font-weight: 500 !important;
    overflow: hidden !important;
    white-space: nowrap !important;
    text-overflow: ellipsis !important;
    line-height: 1.2 !important;
    min-height: 24px !important;
    display: flex !important;
    align-items: center !important;
    cursor: pointer !important;
    transition: all 0.2s ease !important;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    }
  }
  
  .rbc-event-content {
    overflow: hidden !important;
    white-space: nowrap !important;
    text-overflow: ellipsis !important;
    width: 100% !important;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 1rem;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(36, 147, 147, 0.2);
  border-top: 3px solid ${colors.brown};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  color: ${colors.inputFeild};
  font-size: 1rem;
  margin: 0;
`;

// Toolbar Components
const ToolbarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: white;
  border-bottom: 1px solid ${colors.inputBorderWhite};
`;

const ToolbarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ToolbarCenter = styled.div`
  flex: 1;
  text-align: center;
`;

const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const NavigationButton = styled.button`
  width: 32px;
  height: 32px;
  border: 1px solid ${colors.inputBorderWhite};
  background: ${colors.lightWhite};
  color: ${colors.text};
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${colors.inputWhite};
    border-color: ${colors.brown};
  }
`;

const TodayButton = styled.button`
  padding: 6px 12px;
  border: none;
  background: ${colors.brown};
  color: white;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: #1a6b6b;
  }
`;

const MonthLabel = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${colors.text};
  margin: 0;
`;

const ViewButton = styled.button`
  padding: 6px 12px;
  border: 1px solid ${colors.inputBorderWhite};
  background: ${props => props.active ? colors.brown : 'white'};
  color: ${props => props.active ? 'white' : colors.text};
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? '#1a6b6b' : colors.lightWhite};
    border-color: ${colors.brown};
  }
`;

// Event Components
const EventContainer = styled.div`
  color: white;
  border-radius: 4px;
  padding: 2px 4px;
  font-size: 0.7rem;
  font-weight: 500;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  width: 100%;
`;

const EventTitle = styled.div`
  font-weight: 600;
  margin-bottom: 1px;
`;

const EventTime = styled.div`
  font-size: 0.6rem;
  opacity: 0.9;
`;

// Modal Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 0;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.div`
  padding: 1.5rem;
  background: linear-gradient(135deg, ${colors.brown} 0%, #1a6b6b 100%);
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  max-height: 60vh;
  overflow-y: auto;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid ${colors.inputBorderWhite};
  
  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  font-weight: 600;
  color: ${colors.text};
  font-size: 0.9rem;
`;

const DetailValue = styled.span`
  color: ${colors.inputFeild};
  font-size: 0.9rem;
  text-align: right;
  max-width: 60%;
`;

const PhoneLink = styled.a`
  color: ${colors.brown};
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ModalFooter = styled.div`
  padding: 1rem 1.5rem;
  background: ${colors.lightWhite};
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  background: ${colors.brown};
  color: white;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: #1a6b6b;
  }
`;
